import type { LawChangeCatalog } from "@hoalaw/ui";
import { RunConflictError, type HealthCheckRunStatus } from "@/types/runContract";
import { auth } from "@/lib/firebase";
import { UploadError, NETWORK_FAILURE_MESSAGE, uploadErrorFromResponse } from "@/lib/uploadErrors";
import type { UploadCapabilities } from "@/lib/uploadCapabilities";
import { getAnonymousSessionId } from "@/lib/analytics";
import { chatApiVersion } from "@/config/features";
import type {
  AdminUser,
  AdminUsersListResponse,
  AdminUserQAResponse,
  AdminQAPair,
  AdminFeedbackResponse,
  AdminFeedbackListResponse,
  AdminFeedbackSummary,
  FeedbackReasonValue,
  FeedbackReviewRef,
  FeedbackReviewResponse,
  FeedbackReviewStatus,
  AdminUserDocumentsResponse,
  AdminUserAcquisitionResponse,
  AdminUserActivityResponse,
  AdminUserJourneyResponse,
  QAEvaluationResult,
  EvaluationsListResponse,
  EvaluationRun,
  ImprovementProposal,
  ProposalsListResponse,
  ProposalStatusUpdate,
  BatchTriggerResponse,
  EvalDigestResponse,
  EducationPartnerCode,
  EducationPartnerCodeCreate,
  EducationPartnerCodeUpdate,
  HealthRegion,
  HealthStatusResponse,
  HealthRunResponse,
  HealthHistoryResponse,
  HealthDiagnosticsResponse,
  MarketingTask,
  MarketingTaskCreate,
  MarketingTaskExecution,
  MarketingTaskCompletion,
  MarketingSeoPage,
  MarketingGoal,
  MarketingInitiative,
  MarketingStrategyDecision,
  MarketingAnalysisRun,
  MarketingRunTask,
  AnalysisRunTriggerResponse,
  ManualEvidenceCreate,
  ManualEvidenceResponse,
  MarketingTaskComment,
  SeoOpportunity,
  SeoSearchPerformanceResponse,
  SeoContentCoverageResponse,
  SeoCompetitorPage,
} from "@/types/admin";
import { DocumentType, UserDocument } from "@/types/documents";


/** Where a form use came from, for the server's durable form-use record
 *  (form_uses_*). All optional; the server validates each one. */
export interface FormUseFields {
  template_session_id?: string;
  form_action_id?: string;
  entry_surface?: string;
  origin_message_id?: number;
}

const API_BASE_URL = import.meta.env.DEV ? "" : (import.meta.env.VITE_API_BASE_URL || "");
const REGION = "fl";
const CHAT_BASE = "/api/florida/v1";

export interface FeedbackResult {
  message_id: number;
  is_liked: boolean | null;
  success: boolean;
  message: string;
  points_earned: number;
  points: number;
}

export interface UserInfo {
  id: string;
  email: string;
  hoa_name?: string | null;
  subscription_status?: string;
  subscription_id?: string | null;
  subscription_end_date?: string | null;
  points?: number;
  is_admin?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface EmailStreamPreference {
  key: string;
  label: string;
  enabled: boolean;
}

export interface EmailPreferences {
  opted_out: boolean;
  streams: EmailStreamPreference[];
  always_sent: { key: string; label: string }[];
  source?: string | null;
  updated_at?: string | null;
}

export interface SubscriptionStatusResponse {
  is_active: boolean;
  status: string | null;
  end_date: string | null;
  cancel_at_period_end?: boolean;
  /** Backend-computed — see Phase 2/3 of the Education Partner Pilot plan. Optional because it
   * only starts appearing once the backend's redemption table exists. */
  eligible_for_education_discount?: boolean;
}

export interface CancelSubscriptionResponse {
  success: boolean;
  message: string;
  subscription_id: string | null;
  end_date: string | null;
  cancel_at_period_end: boolean;
}

interface ChatbotResponse {
  response: string;
  sources?: string[];
  confidence?: number;
}

export interface ChatQuota {
  /**
   * - `subscriber` — unlimited.
   * - `free`       — free tier on, `remaining` questions left.
   * - `exhausted`  — free tier on, used up. Show the upgrade prompt.
   * - `locked`     — free tier off and not subscribed (subscription required).
   */
  access: "subscriber" | "free" | "exhausted" | "locked" | "open";
  region: string;
  is_subscriber: boolean;
  is_admin: boolean;
  free_tier_enabled: boolean;
  limit: number | null;
  used: number | null;
  remaining: number | null;
}

interface DocumentUploadResponse {
  success: boolean;
  documentId: string;
  message: string;
}

// Matches what the backend actually returns from GET /auth/me and
// PUT /auth/hoa-name — both snake_case, both far looser than this used to
// claim (uid/createdAt/updatedAt were never in either response).
interface UserProfile {
  hoa_name?: string;
  email?: string;
  name?: string;
  /** Durable, cross-device "Association:" selection (P1 staged-persistence
   *  stage 2). */
  association_type?: "hoa_poa" | "condo" | "unsure" | null;
  association_type_source?: string | null;
}

interface HOAUpdateResponse {
  success: boolean;
  message: string;
  hoa_name?: string;
}

interface StripeCustomer {
  id: string;
  email: string;
  name?: string;
  hoa_name?: string | null;
  created: number;
  balance: number;
  currency?: string;
  default_source?: string;
  invoice_prefix?: string;
  invoice_settings?: {
    default_payment_method?: string;
    footer?: string;
  };
}

interface StripeSubscription {
  id: string;
  status: string;
  current_period_start: number;
  current_period_end: number;
  cancel_at_period_end: boolean;
  canceled_at?: number;
  trial_start?: number;
  trial_end?: number;
  plan: {
    id: string;
    amount: number;
    currency: string;
    interval: string;
    interval_count: number;
    nickname?: string;
  };
  latest_invoice?: {
    id: string;
    amount_paid: number;
    amount_due: number;
    status: string;
    created: number;
  };
}

interface PaymentHistory {
  data: Array<{
    id: string;
    amount: number;
    currency: string;
    status: string;
    created: number;
    description?: string;
    receipt_url?: string;
    invoice_id?: string;
  }>;
  has_more: boolean;
  total_count?: number;
}

interface UpcomingInvoice {
  id?: string;
  amount_due: number;
  amount_paid: number;
  currency: string;
  period_start: number;
  period_end: number;
  subtotal: number;
  tax?: number;
  total: number;
  status?: string;
  lines: {
    data: Array<{
      id: string;
      amount: number;
      currency: string;
      description?: string;
      period: {
        start: number;
        end: number;
      };
      plan?: {
        nickname?: string;
        interval: string;
      };
    }>;
  };
}

function normalizeIsAdmin(value: unknown): boolean {
  if (typeof value === "string") {
    return value.trim().toLowerCase() === "true" || value.trim() === "1";
  }
  return value === true || value === 1;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" ? value as Record<string, unknown> : {};
}

async function authFetch(path: string, init: RequestInit = {}) {
  const user = auth.currentUser;
  if (!user) throw new Error("User not authenticated");

  const token = await user.getIdToken();
  const headers = new Headers(init.headers);

  const isFormData = typeof FormData !== "undefined" && init.body instanceof FormData;
  if (init.body && !isFormData && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  headers.set("Authorization", `Bearer ${token}`);

  return fetch(`${API_BASE_URL}${path}`, { ...init, headers });
}

const _REGION_LONG_NAMES: Record<string, string> = { ca: "california", fl: "florida", tx: "texas" };

/** Review-era endpoints use the long region prefix (california/florida/texas)
 *  per the plan's "Region naming, defined once" convention — everywhere else
 *  (forms/communications/library) uses the short ca/fl/tx path param. */
function _regionLongName(region: string): string {
  return _REGION_LONG_NAMES[region.toLowerCase()] || region.toLowerCase();
}

export const fastApiClient = {
  async getLawChanges(region: "ca" | "fl" | "tx", signal?: AbortSignal): Promise<LawChangeCatalog> {
    const response = await authFetch(`/api/law-changes/${region}`, { signal });
    if (!response.ok) throw new Error("Failed to load law changes");
    return response.json();
  },

  async verifyAuth(): Promise<UserInfo> {
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");
    const token = await user.getIdToken();

    let data: Record<string, unknown> | null = null;

    // Step 1: POST /api/auth/verify
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/verify?region=${REGION}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        data = await res.json();
      }
    } catch {
      // fall through to step 2
    }

    // Step 2: GET /api/stripe/customer (fallback)
    if (!data) {
      const res = await fetch(`${API_BASE_URL}/api/stripe/customer?region=${REGION}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to verify user");
      data = await res.json();
    }

    // Step 3: Enrich is_admin from /api/user/me if still missing/null
    const isAdminRaw = (data.is_admin ?? data.isAdmin) as unknown;
    const hasAdminValue = isAdminRaw !== undefined && isAdminRaw !== null;
    let isAdminValue = hasAdminValue ? normalizeIsAdmin(isAdminRaw) : false;

    if (!hasAdminValue) {
      try {
        const meRes = await fetch(`${API_BASE_URL}/api/auth/me?region=${REGION}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (meRes.ok) {
          const meData = await meRes.json() as Record<string, unknown>;
          if (meData.is_admin !== undefined && meData.is_admin !== null) {
            isAdminValue = normalizeIsAdmin(meData.is_admin);
          }
        }
      } catch {
        // enrichment failure is always swallowed
      }
    }

    return {
      id: user.uid,
      email: (data.email as string) || user.email || "",
      hoa_name: data.hoa_name as string | null | undefined,
      subscription_status: data.subscription_status as string | undefined,
      subscription_id: data.subscription_id as string | null | undefined,
      subscription_end_date: data.subscription_end_date as string | null | undefined,
      points: data.points as number | undefined,
      is_admin: isAdminValue,
      created_at: data.created_at as string | undefined,
      updated_at: data.updated_at as string | undefined,
    };
  },

  /** Chat entitlement as the backend sees it. Region-prefixed here — the
   *  California app mounts this router at the bare /api/chat. */
  /** Federated Document Library catalog. Region-agnostic route — the backend
   *  accepts ca/fl/tx and only lists what actually exists for that region. */
  async getDocumentLibraryCatalog(region: string): Promise<import("@/types/library").DocumentLibraryCatalog> {
    const response = await authFetch(
      `/api/document-library/${encodeURIComponent(region)}/catalog`
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error(
        typeof err.detail === "string" ? err.detail : "Failed to load document library catalog"
      );
    }
    return response.json();
  },

  // ==================== COMMUNICATIONS ENDPOINTS ====================
  // Gating split: listCommunicationTypes needs auth only (discovery);
  // draftCommunication/updateCommunicationDraft/redraftCommunicationDraft need
  // an active subscription (402 subscription_required_for_region otherwise);
  // getCommunicationDraft/renderCommunicationDraft are owner-gated only — no
  // subscription required, so reads/exports survive expiry.

  async listCommunicationTypes(region: string): Promise<{
    region: string;
    types: import("@/types/communications").CommunicationTypeInfo[];
  }> {
    const response = await authFetch(`/api/communications/${encodeURIComponent(region)}/types`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error(typeof err.detail === "string" ? err.detail : "Failed to load communication types");
    }
    return response.json();
  },

  async draftCommunication(
    region: string,
    payload: import("@/types/communications").DraftCommunicationRequest
  ): Promise<import("@/types/communications").CommunicationDraft> {
    const response = await authFetch(`/api/communications/${encodeURIComponent(region)}/draft`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error(typeof err.detail === "string" ? err.detail : "Failed to draft communication");
    }
    return response.json();
  },

  async previewCommunication(
    region: string,
    payload: import("@/types/communications").PreviewCommunicationRequest
  ): Promise<import("@/types/communications").CommunicationPreview> {
    const anonymousSessionId = getAnonymousSessionId();
    // Keep the anonymous session id in the JSON payload, not a custom
    // header. The backend already accepts payload.anonymous_session_id for
    // session rate limiting, and avoiding X-Anonymous-Session-Id here keeps
    // this public funnel compatible with older edge CORS header allowlists.
    const response = await fetch(
      `${API_BASE_URL}/api/communications/${encodeURIComponent(region)}/preview`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...payload, anonymous_session_id: anonymousSessionId }),
      }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error(typeof err.detail === "string" ? err.detail : "Failed to generate preview");
    }
    return response.json();
  },

  async getCommunicationDraft(
    region: string,
    draftId: number
  ): Promise<import("@/types/communications").CommunicationDraft> {
    const response = await authFetch(
      `/api/communications/${encodeURIComponent(region)}/drafts/${draftId}`
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error(typeof err.detail === "string" ? err.detail : "Failed to load draft");
    }
    return response.json();
  },

  async updateCommunicationDraft(
    region: string,
    draftId: number,
    payload: import("@/types/communications").UpdateCommunicationDraftRequest
  ): Promise<import("@/types/communications").CommunicationDraft> {
    const response = await authFetch(
      `/api/communications/${encodeURIComponent(region)}/drafts/${draftId}`,
      { method: "PUT", body: JSON.stringify(payload) }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error(typeof err.detail === "string" ? err.detail : "Failed to save draft");
    }
    return response.json();
  },

  async redraftCommunicationDraft(
    region: string,
    draftId: number,
    payload: import("@/types/communications").RedraftCommunicationRequest
  ): Promise<import("@/types/communications").CommunicationDraft> {
    const response = await authFetch(
      `/api/communications/${encodeURIComponent(region)}/drafts/${draftId}/redraft`,
      { method: "POST", body: JSON.stringify(payload) }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error(typeof err.detail === "string" ? err.detail : "Failed to redraft");
    }
    return response.json();
  },

  /** User proof loop (Business guardrails) — owner-gated only, no
   *  subscription required (survives expiry, same as get/render). */
  async submitCommunicationDraftFeedback(
    region: string,
    draftId: number,
    payload: import("@/types/communications").CommunicationFeedbackRequest
  ): Promise<import("@/types/communications").CommunicationDraft> {
    const response = await authFetch(
      `/api/communications/${encodeURIComponent(region)}/drafts/${draftId}/feedback`,
      { method: "POST", body: JSON.stringify(payload) }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error(typeof err.detail === "string" ? err.detail : "Failed to submit feedback");
    }
    return response.json();
  },

  /** format="email" resolves to JSON (EmailRenderResponse); "letter"/"notice"
   * resolve to a DOCX Blob. Callers branch on `format`. */
  async renderCommunicationDraft(
    region: string,
    draftId: number,
    format: import("@/types/communications").OutputFormat
  ): Promise<import("@/types/communications").EmailRenderResponse | Blob> {
    const response = await authFetch(
      `/api/communications/${encodeURIComponent(region)}/drafts/${draftId}/render`,
      { method: "POST", body: JSON.stringify({ format }) }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error(typeof err.detail === "string" ? err.detail : "Failed to render draft");
    }
    return format === "email" ? response.json() : response.blob();
  },

  async uploadReviewDocument(region: string, file: File): Promise<import("@/types/review").ReviewDocument> {
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");
    const token = await user.getIdToken();
    const formData = new FormData();
    formData.append("file", file);

    const response = await fetch(`${API_BASE_URL}/api/${_regionLongName(region)}/v1/review/documents/upload`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error(typeof err.detail === "string" ? err.detail : "Failed to upload document for Review");
    }
    return response.json();
  },

  async listReviewDocuments(region: string): Promise<import("@/types/review").ReviewDocument[]> {
    const response = await authFetch(`/api/${_regionLongName(region)}/v1/review/documents`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error(typeof err.detail === "string" ? err.detail : "Failed to load Review uploads");
    }
    return response.json();
  },

  async getReviewDocument(region: string, documentId: number): Promise<import("@/types/review").ReviewDocument> {
    const response = await authFetch(`/api/${_regionLongName(region)}/v1/review/documents/${documentId}`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error(typeof err.detail === "string" ? err.detail : "Failed to check Review upload status");
    }
    return response.json();
  },

  async deleteReviewDocument(region: string, documentId: number): Promise<void> {
    const response = await authFetch(`/api/${_regionLongName(region)}/v1/review/documents/${documentId}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error(typeof err.detail === "string" ? err.detail : "Failed to delete Review upload");
    }
  },

  getReviewAnalyzeStreamUrl(region: string): string {
    return `${API_BASE_URL}/api/${_regionLongName(region)}/v1/analysis/analyze`;
  },

  async listMatters(region: string): Promise<{ matters: import("@/types/review").ReviewMatterSummary[] }> {
    const response = await authFetch(`/api/${_regionLongName(region)}/v1/analysis/matters`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error(typeof err.detail === "string" ? err.detail : "Failed to load Reviews");
    }
    return response.json();
  },

  async getMatter(region: string, matterId: number): Promise<import("@/types/review").ReviewMatterDetail> {
    const response = await authFetch(`/api/${_regionLongName(region)}/v1/analysis/matters/${matterId}`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error(typeof err.detail === "string" ? err.detail : "Failed to load Review");
    }
    return response.json();
  },

  async deleteMatter(region: string, matterId: number): Promise<void> {
    const response = await authFetch(`/api/${_regionLongName(region)}/v1/analysis/matters/${matterId}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error(typeof err.detail === "string" ? err.detail : "Failed to delete Review");
    }
  },

  async rerunMatterAnalysis(
    region: string,
    matterId: number,
    analysisId: number
  ): Promise<import("@/types/review").ReviewResult> {
    const response = await authFetch(`/api/${_regionLongName(region)}/v1/analysis/matters/${matterId}/rerun`, {
      method: "POST",
      body: JSON.stringify({ analysis_id: analysisId }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error(typeof err.detail === "string" ? err.detail : "Failed to re-run Review");
    }
    return response.json();
  },

  async uploadReviewPreview(
    region: string,
    file: File,
    intent?: string
  ): Promise<import("@/types/reviewPreview").ReviewPreviewUploadResponse> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("anonymous_session_id", getAnonymousSessionId());
    if (intent) formData.append("intent", intent);

    const response = await fetch(`${API_BASE_URL}/api/${_regionLongName(region)}/v1/review-preview/upload`, {
      method: "POST",
      body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error(typeof err.detail === "string" ? err.detail : "Failed to upload document for preview");
    }
    return response.json();
  },

  async getReviewPreview(
    region: string,
    previewId: string
  ): Promise<import("@/types/reviewPreview").ReviewPreviewPublic> {
    const response = await fetch(`${API_BASE_URL}/api/${_regionLongName(region)}/v1/review-preview/${previewId}`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error(typeof err.detail === "string" ? err.detail : "Failed to load preview");
    }
    return response.json();
  },

  async claimReviewPreview(
    region: string,
    previewId: string
  ): Promise<import("@/types/reviewPreview").ClaimReviewPreviewResponse> {
    const response = await authFetch(`/api/${_regionLongName(region)}/v1/review-preview/${previewId}/claim`, {
      method: "POST",
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error(typeof err.detail === "string" ? err.detail : "Failed to claim preview");
    }
    return response.json();
  },

  async getAnalyticsFunnel(params: {
    events?: string[];
    region?: string;
    startDate?: string; // ISO datetime
    endDate?: string; // ISO datetime
  } = {}): Promise<import("@/types/admin").AnalyticsFunnelResponse> {
    const queryParams = new URLSearchParams();
    if (params.events?.length) queryParams.append("events", params.events.join(","));
    if (params.region) queryParams.append("region", params.region);
    if (params.startDate) queryParams.append("start_date", params.startDate);
    if (params.endDate) queryParams.append("end_date", params.endDate);

    const response = await authFetch(`/api/admin/analytics/funnel?${queryParams}`);
    if (response.status === 403) {
      throw new Error("Admin access required");
    }
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error(typeof err.detail === "string" ? err.detail : "Failed to load analytics funnel");
    }
    return response.json();
  },

  async getAnalyticsFormsTemplates(params: {
    region?: string;
    startDate?: string; // ISO datetime
  } = {}): Promise<import("@/types/admin").FormsTemplatesAnalyticsResponse> {
    const queryParams = new URLSearchParams();
    if (params.region) queryParams.append("region", params.region);
    if (params.startDate) queryParams.append("start_date", params.startDate);
    const response = await authFetch(`/api/admin/analytics/forms-templates?${queryParams}`);
    if (response.status === 403) {
      throw new Error("Admin access required");
    }
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error(typeof err.detail === "string" ? err.detail : "Failed to load Forms & Templates analytics");
    }
    return response.json();
  },

  // ----- Question Intelligence (Product Analytics → Questions; Message Evaluation chips) -----

  async getAnalyticsQuestions(params: {
    region?: string;
    startDate?: string; // ISO datetime
    topic?: string; // narrow every section to one topic ("none" = no topic)
    intent?: string; // narrow every section to one intent
  } = {}): Promise<import("@/types/questionAnalytics").QuestionsAnalyticsResponse> {
    const queryParams = new URLSearchParams();
    if (params.region) queryParams.append("region", params.region);
    if (params.startDate) queryParams.append("start_date", params.startDate);
    if (params.topic) queryParams.append("topic", params.topic);
    if (params.intent) queryParams.append("intent", params.intent);
    const response = await authFetch(`/api/admin/analytics/questions?${queryParams}`);
    if (response.status === 403) throw new Error("Admin access required");
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error(typeof err.detail === "string" ? err.detail : "Failed to load Questions analytics");
    }
    return response.json();
  },

  async getProductActivity(region?: string): Promise<import("@/types/questionAnalytics").ProductActivitySummary> {
    const query = region ? `?${new URLSearchParams({ region })}` : "";
    const response = await authFetch(`/api/admin/analytics/product-activity${query}`);
    if (!response.ok) throw new Error("Failed to load product activity");
    return response.json();
  },

  async getQuestionClassificationStatus(): Promise<import("@/types/questionAnalytics").QuestionClassificationStatus> {
    const response = await authFetch("/api/admin/analytics/questions/classification/status");
    if (response.status === 403) throw new Error("Admin access required");
    if (!response.ok) throw new Error("Failed to load classification status");
    return response.json();
  },

  /** Put permanently-failed classifications back in the queue (attempts reset). */
  async retryFailedQuestionClassifications(): Promise<{ retried: number }> {
    const response = await authFetch("/api/admin/analytics/questions/classification/retry-failed", { method: "POST" });
    if (!response.ok) throw new Error("Failed to retry failed classifications");
    return response.json();
  },

  /** "Process pending". Throws ConfirmEstimatedCostError when the server asks
   *  for the estimated cost to be confirmed; resend with confirm=true. */
  async runQuestionClassification(
    confirmEstimatedCost = false,
  ): Promise<import("@/types/questionAnalytics").QuestionClassificationRunResult> {
    const response = await authFetch("/api/admin/analytics/questions/classification/run", {
      method: "POST",
      body: JSON.stringify({ confirm_estimated_cost: confirmEstimatedCost }),
    });
    if (response.status === 409) {
      const err = await response.json().catch(() => ({})) as { detail?: Record<string, unknown> };
      if (err.detail?.code === "confirm_estimated_cost") {
        const { ConfirmEstimatedCostError } = await import("@/types/questionAnalytics");
        throw new ConfirmEstimatedCostError(Number(err.detail.estimated_run_cost_usd), Number(err.detail.backlog));
      }
      throw new Error("Classification is turned off on the server.");
    }
    if (!response.ok) throw new Error("Failed to start classification");
    return response.json();
  },

  /** One page of Message Evaluation's review list (history-first, filtered). */
  async listQuestionsForReview(params: {
    filters: import("@/types/questionReview").QuestionReviewFilters;
    region?: string;
    startDate?: string;
    cursor?: string | null;
    limit?: number;
  }): Promise<import("@/types/questionReview").QuestionReviewList> {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params.filters)) {
      if (value === undefined || value === null) continue;
      if (Array.isArray(value)) value.forEach((v) => query.append(key, String(v)));
      else query.set(key, String(value));
    }
    if (params.region) query.set("region", params.region);
    if (params.startDate) query.set("start_date", params.startDate);
    if (params.cursor) query.set("cursor", params.cursor);
    if (params.limit) query.set("limit", String(params.limit));
    const response = await authFetch(`/api/admin/question-intelligence/questions?${query}`);
    if (!response.ok) throw new Error("Failed to load the questions");
    return response.json();
  },

  async getQuestionLabels(region: string, messageId: number): Promise<import("@/types/questionReview").QuestionLabelsState> {
    const response = await authFetch(
      `/api/admin/question-intelligence/messages/${encodeURIComponent(region)}/${messageId}/labels`,
    );
    if (response.status === 404) {
      const err = await response.json().catch(() => ({})) as { detail?: { code?: string } };
      const { QuestionNotCapturedError } = await import("@/types/questionReview");
      throw new QuestionNotCapturedError(err.detail?.code || "not_found");
    }
    if (!response.ok) throw new Error("Failed to load question labels");
    return response.json();
  },

  async correctQuestionLabel(
    region: string, messageId: number, field: string, value: string,
  ): Promise<import("@/types/questionReview").QuestionLabelsState> {
    const response = await authFetch(
      `/api/admin/question-intelligence/messages/${encodeURIComponent(region)}/${messageId}/labels`,
      { method: "POST", body: JSON.stringify({ field, value }) },
    );
    if (!response.ok) throw new Error("That value isn't allowed for this label.");
    return response.json();
  },

  async resetQuestionLabel(
    region: string, messageId: number, field: string,
  ): Promise<import("@/types/questionReview").QuestionLabelsState> {
    const response = await authFetch(
      `/api/admin/question-intelligence/messages/${encodeURIComponent(region)}/${messageId}/labels/${encodeURIComponent(field)}`,
      { method: "DELETE" },
    );
    if (!response.ok) throw new Error("Failed to reset the label");
    return response.json();
  },

  async setQuestionExclusion(
    region: string, messageId: number, reason: string | null,
  ): Promise<import("@/types/questionReview").QuestionLabelsState> {
    const url = `/api/admin/question-intelligence/messages/${encodeURIComponent(region)}/${messageId}/exclusion`;
    const response = reason
      ? await authFetch(url, { method: "POST", body: JSON.stringify({ reason }) })
      : await authFetch(url, { method: "DELETE" });
    if (!response.ok) throw new Error("Failed to update the exclusion");
    return response.json();
  },

  async getAnalyticsCost(region?: string): Promise<import("@/types/admin").AnalyticsCostResponse> {
    const query = region ? `?${new URLSearchParams({ region })}` : "";
    const response = await authFetch(`/api/admin/analytics/cost${query}`);
    if (response.status === 403) {
      throw new Error("Admin access required");
    }
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error(typeof err.detail === "string" ? err.detail : "Failed to load cost analytics");
    }
    return response.json();
  },

  /** Admin-home Customer Journey cohort strip -- a genuine per-cohort
   * fixed-window funnel (New accounts -> Activated -> Paid), never a
   * same-period ratio. Admin only. */
  async getCustomerJourneyCohort(region?: string): Promise<import("@/types/admin").CustomerJourneyCohort> {
    const query = region ? `?${new URLSearchParams({ region })}` : "";
    const response = await authFetch(`/api/admin/analytics/customer-journey${query}`);
    if (response.status === 403) {
      throw new Error("Admin access required");
    }
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error(typeof err.detail === "string" ? err.detail : "Failed to load customer journey");
    }
    return response.json();
  },

  async getEmailPreferenceStats(region?: string): Promise<import("@/types/admin").EmailPreferenceStats> {
    const queryParams = new URLSearchParams();
    if (region) queryParams.append("region", region);
    const response = await authFetch(`/api/admin/email-preferences/stats?${queryParams}`);
    if (response.status === 403) {
      throw new Error("Admin access required");
    }
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error(typeof err.detail === "string" ? err.detail : "Failed to load email preference stats");
    }
    return response.json();
  },

  /** Summary rollups for the admin Email page (Admin only). */
  async getEmailStats(params: { region?: string; days?: number } = {}): Promise<import("@/types/admin").AdminEmailStats> {
    const queryParams = new URLSearchParams();
    if (params.region) queryParams.append("region", params.region);
    if (params.days) queryParams.append("days", String(params.days));
    const response = await authFetch(`/api/admin/email/stats?${queryParams}`);
    if (response.status === 403) {
      throw new Error("Admin access required");
    }
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error(typeof err.detail === "string" ? err.detail : "Failed to load email stats");
    }
    return response.json();
  },

  /** Filterable notice_log listing for the admin Email page's activity log (Admin only). */
  async getEmailActivity(params: {
    region?: string;
    status?: string;
    noticeType?: string;
    startDate?: string; // ISO datetime
    endDate?: string; // ISO datetime
    limit?: number;
    offset?: number;
  } = {}): Promise<import("@/types/admin").AdminEmailActivityResponse> {
    const queryParams = new URLSearchParams();
    if (params.region) queryParams.append("region", params.region);
    if (params.status) queryParams.append("status", params.status);
    if (params.noticeType) queryParams.append("notice_type", params.noticeType);
    if (params.startDate) queryParams.append("start_date", params.startDate);
    if (params.endDate) queryParams.append("end_date", params.endDate);
    if (params.limit) queryParams.append("limit", String(params.limit));
    if (params.offset) queryParams.append("offset", String(params.offset));
    const response = await authFetch(`/api/admin/email/activity?${queryParams}`);
    if (response.status === 403) {
      throw new Error("Admin access required");
    }
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error(typeof err.detail === "string" ? err.detail : "Failed to load email activity");
    }
    return response.json();
  },

  /** Per-notice_type conversion/click/unsubscribe performance for the admin Email
   * page's Performance section (Admin only). */
  async getEmailPerformance(params: {
    region?: string;
    days?: number;
    stage?: string;
  } = {}): Promise<import("@/types/admin").AdminEmailPerformanceResponse> {
    const queryParams = new URLSearchParams();
    if (params.region) queryParams.append("region", params.region);
    if (params.days) queryParams.append("days", String(params.days));
    if (params.stage) queryParams.append("stage", params.stage);
    const response = await authFetch(`/api/admin/email/performance?${queryParams}`);
    if (response.status === 403) {
      throw new Error("Admin access required");
    }
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error(typeof err.detail === "string" ? err.detail : "Failed to load email performance");
    }
    return response.json();
  },

  async updateAdminEmailPreferences(
    userId: string,
    body: { marketing_opt_out?: boolean; streams?: string[] },
    region?: string,
  ): Promise<AdminUser> {
    const queryParams = region ? `?region=${encodeURIComponent(region)}` : "";
    const response = await authFetch(`/api/admin/users/${userId}/email-preferences${queryParams}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
    if (response.status === 403) {
      throw new Error("Admin access required");
    }
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error(typeof err.detail === "string" ? err.detail : "Failed to update email preferences");
    }
    return response.json();
  },

  async getChatQuota(): Promise<ChatQuota> {
    const response = await authFetch("/api/florida/v1/chat/quota");
    if (!response.ok) {
      throw new Error("Failed to fetch chat quota");
    }
    return response.json();
  },

  async getCurrentUser(): Promise<UserInfo> {
    return fastApiClient.verifyAuth();
  },

  async getSubscriptionStatus(): Promise<SubscriptionStatusResponse> {
    const response = await authFetch(`/api/subscription/status?region=${REGION}`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to fetch subscription status");
    }
    return response.json();
  },

  async createCheckoutSession(params: {
    success_url: string;
    cancel_url: string;
    hoa_name: string;
    plan: string;
    promo_code?: string | null;
    // Proof of affirmative consent to the auto-renewal terms, retained server-side for 3 years
    // under Cal. Bus. & Prof. Code §17602(a)(6). The IP and user-agent are captured by the API
    // from the request itself rather than trusted from here.
    consent?: {
      accepted_auto_renewal: boolean;
      accepted_terms: boolean;
      terms_version: string;
      plan_id: string;
      disclosure_text: string;
    };
  }): Promise<string> {
    const response = await authFetch("/api/subscription/create-checkout-session", {
      method: "POST",
      body: JSON.stringify({ ...params, region: REGION }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to create checkout session");
    }
    const data = await response.json() as Record<string, unknown>;
    const url = (data.url || data.checkout_url || data.session_url) as string | undefined;
    if (!url) throw new Error("Checkout session URL missing");
    return url;
  },

  /** No auth required — a participant may not be logged in yet when they land on the
   * /education/redeem confirmation screen. */
  async getEducationCode(token: string): Promise<{ valid: boolean; provider_name?: string; trial_days?: number }> {
    const response = await fetch(`${API_BASE_URL}/api/subscription/education-code?token=${encodeURIComponent(token)}&region=${REGION}`);
    if (!response.ok) {
      return { valid: false };
    }
    return response.json();
  },

  async createEducationCheckoutSession(params: {
    success_url: string;
    cancel_url: string;
    education_token: string;
  }): Promise<string> {
    const response = await authFetch("/api/subscription/create-checkout-session", {
      method: "POST",
      body: JSON.stringify({ ...params, plan: "annual", region: REGION }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to start complimentary access");
    }
    const data = await response.json() as Record<string, unknown>;
    const url = (data.url || data.checkout_url || data.session_url) as string | undefined;
    if (!url) throw new Error("Checkout session URL missing");
    return url;
  },

  /** /admin/education-partners — manages partner codes across all three regions, regardless of
   * which app's admin panel you're logged into (admin status is checked across all regions
   * server-side). Not gated to REGION like the rest of this file. */
  async createEducationPartnerCode(params: EducationPartnerCodeCreate): Promise<EducationPartnerCode> {
    const response = await authFetch("/api/admin/education-partner-codes", {
      method: "POST",
      body: JSON.stringify(params),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to create partner code");
    }
    return response.json();
  },

  async listEducationPartnerCodes(region?: string): Promise<EducationPartnerCode[]> {
    const queryParams = new URLSearchParams();
    if (region) queryParams.append("region", region);
    const response = await authFetch(`/api/admin/education-partner-codes?${queryParams}`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to load partner codes");
    }
    return response.json();
  },

  async updateEducationPartnerCode(id: number, params: EducationPartnerCodeUpdate): Promise<EducationPartnerCode> {
    const response = await authFetch(`/api/admin/education-partner-codes/${id}`, {
      method: "PATCH",
      body: JSON.stringify(params),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to update partner code");
    }
    return response.json();
  },

  /** /admin/health/* — System Health synthetic probe status, same cross-region
   * scope as education partners above (not gated to REGION). */
  async getHealthStatus(): Promise<HealthStatusResponse> {
    const response = await authFetch("/api/admin/health/status");
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to load system health");
    }
    return response.json();
  },

  /** Requests chat and document checks in the worker (202, run contract) -- poll
   * it with getHealthRun. An
   * identical run already active is reused; a partly overlapping one -> RunConflictError. */
  async runHealthCheck(region: HealthRegion | "all" = "all", scope: string = "regional"): Promise<HealthRunResponse> {
    const params = new URLSearchParams({ region, scope });
    const response = await authFetch(`/api/admin/health/run?${params.toString()}`, { method: "POST" });
    if (response.status === 409) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new RunConflictError(
        (err.detail as string) || "Another check run is already covering some of these checks.",
        Number(err.conflicting_run_id),
      );
    }
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to trigger health check");
    }
    return response.json();
  },

  async getHealthRun(runId: number): Promise<HealthCheckRunStatus> {
    const response = await authFetch(`/api/admin/health/runs/${runId}`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to load the check run");
    }
    return response.json();
  },

  /** view="activity" (default) is the dashboard's "Recent activity" list;
   * view="all" is the raw per-check history behind "Show all checks". */
  async getHealthHistory(params: { view?: "activity" | "all"; limit?: number } = {}): Promise<HealthHistoryResponse> {
    const query = new URLSearchParams();
    query.set("view", params.view ?? "activity");
    if (params.limit) query.set("limit", String(params.limit));
    const response = await authFetch(`/api/admin/health/history?${query.toString()}`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to load health history");
    }
    return response.json();
  },

  /** Tier 2 drill-down -- DB/schema/per-region index detail, only fetched
   * when a "View diagnostics" panel is actually expanded. */
  async getHealthDiagnostics(): Promise<HealthDiagnosticsResponse> {
    const response = await authFetch("/api/admin/health/diagnostics");
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to load diagnostics");
    }
    return response.json();
  },

  async createCustomerPortalSession(returnUrl: string): Promise<string> {
    const response = await authFetch(`/api/stripe/create-portal-session?region=${REGION}`, {
      method: "POST",
      body: JSON.stringify({ return_url: returnUrl }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to create customer portal session");
    }
    const data = await response.json() as Record<string, unknown>;
    if (!data.url) throw new Error("Customer portal URL missing");
    return data.url as string;
  },

  async cancelSubscription(): Promise<CancelSubscriptionResponse> {
    const response = await authFetch(`/api/subscription/cancel?region=${REGION}`, { method: "POST" });
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to cancel subscription");
    }
    return response.json();
  },

  // Non-streaming authenticated send helper; live ChatbotContext send behavior remains app-specific.
  async sendChatMessage(message: string): Promise<ChatbotResponse> {
    const response = await authFetch(`${CHAT_BASE}/chat`, {
      method: "POST",
      body: JSON.stringify({ message }),
    });
    if (!response.ok) {
      throw new Error("Failed to send message to AI");
    }
    return response.json();
  },

  // Soft-delete a chat (user action). Hides it from history; row retained server-side.
  async deleteChat(chatId: string | number): Promise<void> {
    const response = await authFetch(`${CHAT_BASE}/${chatId}`, { method: "DELETE" });
    if (!response.ok) {
      throw new Error("Failed to delete chat");
    }
  },

  // Chat history list for the sidebar. Region-scoped by path; uses authFetch so it
  // shares the app's base-URL resolution (relative in dev → Vite proxy) + auth header.
  async getChatHistory() {
    const response = await authFetch(`${CHAT_BASE}/history`);
    if (!response.ok) {
      throw new Error("Failed to fetch chat history");
    }
    return response.json();
  },

  // Fetch a single chat's full transcript. Sanctioned replacement for the
  // raw fetchChatById in ChatbotContext (caller rewire is a later phase).
  async getChatById(chatId: string | number) {
    const response = await authFetch(`${CHAT_BASE}/${chatId}`);
    if (!response.ok) {
      throw new Error("Failed to fetch chat");
    }
    return response.json();
  },

  // Document Management
  // NOTE: this and every other call in this section used to hit the
  // unprefixed /api/documents(/...)  and /api/user-documents(/...)  routes,
  // which are hardcoded on the backend to the California Document model
  // (app/api/router.py mounts them with no region param). Since this app
  // shares the same backend host as California, every Florida governing-
  // document upload/list/delete/view and "ask about your documents" query
  // was silently operating against documents_ca instead of documents_fl.
  // Fixed to use this region's own /api/florida/v1/... routers (florida/
  // documents.py, florida/user_documents.py), matching how
  // setDocumentSearchInclusion below was already (correctly) written.
  /** Throws UploadError: "rejected" (with the server's reason) or "network" (no response). */
  async uploadDocument(file: File, documentType?: string, uploadAttemptId?: string) {
    const formData = new FormData();
    formData.append("file", file);
    if (documentType) formData.append("document_type", documentType);
    // Telemetry correlation only (upload funnel) — not an idempotency key.
    if (uploadAttemptId) formData.append("upload_attempt_id", uploadAttemptId);
    let response: Response;
    try {
      response = await authFetch(`${CHAT_BASE}/documents/upload`, {
        method: "POST",
        body: formData,
      });
    } catch (error) {
      if (error instanceof Error && error.message === "User not authenticated") throw error;
      throw new UploadError("network", NETWORK_FAILURE_MESSAGE);
    }
    if (!response.ok) throw await uploadErrorFromResponse(response);
    return response.json();
  },

  /** Upload a new file to replace an existing document. The current one stays in use until
   *  the replacement is ready; a failed replacement changes nothing. Throws UploadError. */
  async cancelReplacement(documentId: string) {
    const response = await authFetch(`${CHAT_BASE}/documents/${documentId}/cancel-replacement`, { method: "POST" });
    if (!response.ok) throw await uploadErrorFromResponse(response);
    return response.json();
  },

  async replaceDocument(documentId: string, file: File, uploadAttemptId?: string) {
    const formData = new FormData();
    formData.append("file", file);
    if (uploadAttemptId) formData.append("upload_attempt_id", uploadAttemptId);
    let response: Response;
    try {
      response = await authFetch(`${CHAT_BASE}/documents/${documentId}/replace`, {
        method: "POST",
        body: formData,
      });
    } catch (error) {
      if (error instanceof Error && error.message === "User not authenticated") throw error;
      throw new UploadError("network", NETWORK_FAILURE_MESSAGE);
    }
    if (!response.ok) throw await uploadErrorFromResponse(response);
    return response.json();
  },

  /** Change a document's display name (the server keeps the file extension). */
  async renameDocument(documentId: string, filename: string): Promise<{ filename: string }> {
    const response = await authFetch(`${CHAT_BASE}/documents/${documentId}`, {
      method: "PATCH",
      body: JSON.stringify({ filename }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      const detail = body?.detail;
      throw new Error(typeof detail === "string" ? detail : "Couldn't rename this document. Please try again.");
    }
    return response.json();
  },

  /** The region's upload capability manifest (public). */
  async getUploadCapabilities(): Promise<UploadCapabilities> {
    const response = await fetch(`${API_BASE_URL}${CHAT_BASE}/documents/upload-capabilities`);
    if (!response.ok) throw new Error("Failed to load upload capabilities");
    return response.json();
  },

  async getUserDocuments() {
    const response = await authFetch(`${CHAT_BASE}/documents`);
    if (!response.ok) throw new Error("Failed to fetch documents");
    return response.json();
  },

  /** Toggle whether a document is read by the "Search governing documents"
   *  document-mode chat. Region-prefixed — this app mounts its own document
   *  router, unlike California's shared /api/documents. */
  async setDocumentSearchInclusion(documentId: string | number, excluded: boolean) {
    const response = await authFetch(`${CHAT_BASE}/documents/${documentId}/search-inclusion`, {
      method: "PATCH",
      body: JSON.stringify({ excluded }),
    });
    if (!response.ok) {
      throw new Error("Failed to update document search inclusion");
    }
    return response.json();
  },

  /**
   * Re-enqueue optimization for a document stuck in a retryable terminal
   * state (compression_timeout / compression_internal_error) after the
   * backend's own automatic retries were exhausted. Only valid for those two
   * statuses — see backend PROJECT.md's PDF-compression plan.
   */
  async retryPdfOptimization(documentId: string | number) {
    const response = await authFetch(`${CHAT_BASE}/documents/${documentId}/retry-optimization`, {
      method: "POST",
    });
    if (!response.ok) {
      let errorMessage = "Failed to retry document processing";
      try {
        const errorData = await response.json();
        errorMessage = errorData.detail || errorMessage;
      } catch {
        // Could not parse response body
      }
      throw new Error(errorMessage);
    }
    return response.json();
  },

  async getDocumentUrl(documentId: string) {
    const response = await authFetch(`${CHAT_BASE}/documents/${documentId}/url`);
    if (!response.ok) throw new Error("Failed to fetch document URL");
    const data = await response.json();
    return data.url;
  },

  async deleteDocument(documentId: string) {
    const response = await authFetch(`${CHAT_BASE}/documents/${documentId}`, { method: "DELETE" });
    if (!response.ok) throw new Error("Failed to delete document");
  },

  async askUserDocument(message: string, chatId?: number, documentIds?: number[]) {
    const response = await authFetch(`${CHAT_BASE}/user-documents/ask`, {
      method: "POST",
      body: JSON.stringify({ message, chat_id: chatId, document_ids: documentIds }),
    });
    if (!response.ok) throw new Error("Failed to ask question about documents");
    return response.json();
  },

  getUserDocumentStreamUrl() {
    return `${API_BASE_URL}${CHAT_BASE}/user-documents/ask/stream`;
  },

  normalizeDocuments(response: unknown): UserDocument[] {
    const responseRecord = asRecord(response);
    const docs = Array.isArray(response)
      ? response
      : Array.isArray(responseRecord.documents)
        ? responseRecord.documents
        : Array.isArray(responseRecord.data)
          ? responseRecord.data
          : [];

    return docs.map((rawDoc) => {
      const doc = asRecord(rawDoc);
      const id = doc.id;
      const processingStatus = typeof doc.processing_status === "string" ? doc.processing_status : undefined;

      return {
        id: String(id || ""),
        filename: typeof doc.filename === "string" ? doc.filename : "Unknown",
        type: (typeof doc.document_type === "string" ? doc.document_type : DocumentType.DECLARATION_COVENANTS) as DocumentType,
        size: typeof doc.size === "number" ? doc.size : 0,
        uploadDate: typeof doc.created_at === "string" ? doc.created_at : "",
        url: typeof doc.view_url === "string" ? doc.view_url : typeof doc.url === "string" ? doc.url : undefined,
        status: processingStatus === "completed" || processingStatus === "ocr_completed" ? "uploaded" : processingStatus === "failed" ? "error" : "processing",
        processing_status: (processingStatus || "pending") as UserDocument["processing_status"],
        document_id: id ? parseInt(String(id)) : undefined,
        excludedFromSearch: doc.excluded_from_search === true,
        // Set only when confirmed present in the FAISS index as of the most
        // recent rebuild — see Document.indexed_at (backend app/db/models.py).
        // processing_status reaching completed/ocr_completed does not
        // guarantee this on its own.
        indexedAt: typeof doc.indexed_at === "string" ? doc.indexed_at : null,
        lastError: typeof doc.last_error === "string" ? doc.last_error : null,
        replacementIndexAttempts: typeof doc.replacement_index_attempts === "number" ? doc.replacement_index_attempts : 0,
        replacementIndexError: typeof doc.replacement_index_error === "string" ? doc.replacement_index_error : null,
        replacesDocumentId: doc.replaces_document_id != null ? String(doc.replaces_document_id) : null,
        uploadAttemptId: typeof doc.upload_attempt_id === "string" ? doc.upload_attempt_id : null,
      };
    });
  },

  async uploadDocumentWithType(file: File, documentType: string, uploadAttemptId?: string) {
    return this.uploadDocument(file, documentType, uploadAttemptId);
  },

  async getUserDocumentsByType() {
    return this.getUserDocuments();
  },

  async fetchDocumentFileBlob(documentId: string): Promise<Blob> {
    const response = await authFetch(`${CHAT_BASE}/documents/${documentId}/file`);
    if (!response.ok) throw new Error("Failed to fetch document file");
    return response.blob();
  },

  async fetchUserDocumentBlob(document: UserDocument): Promise<Blob> {
    const id = document.id;
    const attempts: string[] = [];

    if (id) {
      try {
        return await this.fetchDocumentFileBlob(id);
      } catch (e) {
        attempts.push(e instanceof Error ? e.message : String(e));
      }
    }

    if (document.url) {
      try {
        const res = await fetch(document.url, {
          headers: { Accept: "application/pdf,application/octet-stream,*/*" },
        });
        if (res.ok) return await res.blob();
        attempts.push(`open url: ${res.status}`);
      } catch (e) {
        attempts.push(e instanceof Error ? e.message : String(e));
      }
    }

    if (id) {
      try {
        const presigned = await this.getDocumentUrl(id);
        const res = await fetch(presigned, {
          headers: { Accept: "application/pdf,application/octet-stream,*/*" },
        });
        if (res.ok) return await res.blob();
        attempts.push(`presigned: ${res.status}`);
      } catch (e) {
        attempts.push(e instanceof Error ? e.message : String(e));
      }
    }

    throw new Error(attempts.length ? attempts.join(" · ") : "Could not load document");
  },

  async getStripeCustomer(): Promise<StripeCustomer> {
    const response = await authFetch(`/api/stripe/customer?region=${REGION}`);

    if (!response.ok) {
      throw new Error("Failed to fetch customer information");
    }

    return response.json();
  },

  async getSubscriptionDetails(): Promise<StripeSubscription> {
    const response = await authFetch(`/api/stripe/subscription?region=${REGION}`);

    if (!response.ok) {
      throw new Error("Failed to fetch subscription details");
    }

    return response.json();
  },

  async getPaymentHistory(limit: number = 10): Promise<PaymentHistory> {
    const response = await authFetch(`/api/stripe/payments?limit=${limit}&region=${REGION}`);

    if (!response.ok) {
      throw new Error("Failed to fetch payment history");
    }

    return response.json();
  },

  async getUpcomingInvoice(): Promise<UpcomingInvoice> {
    const response = await authFetch(`/api/stripe/upcoming-invoice?region=${REGION}`);

    if (!response.ok) {
      throw new Error("Failed to fetch upcoming invoice");
    }

    return response.json();
  },

  async updateSubscription(action: string, data?: Record<string, unknown>): Promise<Record<string, unknown>> {
    const response = await authFetch(`/api/stripe/subscription/update?region=${REGION}`, {
      method: "POST",
      body: JSON.stringify({ action, ...data }),
    });

    if (!response.ok) {
      throw new Error("Failed to update subscription");
    }

    return response.json();
  },

  /** There is no dedicated GET for this — /auth/me already returns hoa_name
   *  alongside the rest of the user record, and is already region-aware.
   *  The route this used to call, /api/user/profile, does not exist on the
   *  backend at all; this method has never worked. As of the Association:
   *  control, it also returns association_type / association_type_source. */
  async getUserProfile(): Promise<UserProfile> {
    const response = await authFetch(`/api/auth/me?region=${REGION}`);

    if (!response.ok) {
      throw new Error("Failed to fetch user profile");
    }

    return response.json();
  },

  /** Persists the "Association:" control's selection as a durable,
   *  cross-device profile fact. Session-scoped persistence (localStorage)
   *  happens separately in ChatbotContext regardless of whether this call
   *  succeeds, so a transient failure here doesn't lose the user's
   *  in-session choice. */
  async updateAssociationType(
    associationType: "hoa_poa" | "condo" | "unsure"
  ): Promise<{ success: boolean; association_type: string; association_type_source: string }> {
    const response = await authFetch(`/api/auth/association-type?region=${REGION}`, {
      method: "PUT",
      body: JSON.stringify({ association_type: associationType }),
    });

    if (!response.ok) {
      throw new Error("Failed to update association type");
    }

    return response.json();
  },

  /** PUT /auth/hoa-name is a single shared route across all three regions,
   *  not region-prefixed like most of this client — region has to be an
   *  explicit query param. The route this used to call, /api/user/hoa-name,
   *  does not exist on the backend at all; this method has never worked,
   *  which is why the page fell back to writing the name into localStorage
   *  instead. */
  async updateHOAName(hoaName: string): Promise<HOAUpdateResponse> {
    const response = await authFetch(`/api/auth/hoa-name?region=${REGION}`, {
      method: "PUT",
      body: JSON.stringify({ hoa_name: hoaName }),
    });

    if (!response.ok) {
      throw new Error("Failed to update HOA name");
    }

    return response.json();
  },

  async getMyEmailPreferences(): Promise<EmailPreferences> {
    const response = await authFetch(`/api/email/me?region=${REGION}`);
    if (!response.ok) {
      throw new Error("Failed to load email preferences");
    }
    return response.json();
  },

  async updateMyEmailPreferences(streams: string[]): Promise<EmailPreferences> {
    const response = await authFetch(`/api/email/me?region=${REGION}`, {
      method: "PUT",
      body: JSON.stringify({ streams }),
    });
    if (!response.ok) {
      throw new Error("Failed to save email preferences");
    }
    return response.json();
  },

  async healthCheck(): Promise<{
    status: string;
    seo_topic_registry?: { schema_version: number; registry_version: number; checksum: string };
  }> {
    const response = await fetch(`${API_BASE_URL}/health`);

    if (!response.ok) {
      throw new Error("API health check failed");
    }

    return response.json();
  },

  async getAuthToken(): Promise<string> {
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");
    return user.getIdToken();
  },

  getStreamUrl(): string {
    return `${API_BASE_URL}${CHAT_BASE}/user-documents/ask/stream`;
  },

  async submitFeedback(messageId: number, isLiked: boolean): Promise<FeedbackResult> {
    const res = await authFetch(`/api/chat/feedback`, {
      method: "POST",
      body: JSON.stringify({ message_id: messageId, is_liked: isLiked, region: REGION }),
    });
    if (!res.ok) throw new Error("Failed to submit feedback");
    return res.json();
  },

  /**
   * Get user points
   */
  async getUserPoints(): Promise<{
    points: number;
    user_id: string;
  }> {
    const response = await authFetch(`/api/chat/user/points`);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error("User not found");
      }
      if (response.status === 401) {
        throw new Error("Unauthorized");
      }
      throw new Error(`Failed to fetch points: ${response.statusText}`);
    }

    return response.json();
  },

  async submitFeedbackReason(
    messageId: number,
    reason: "incorrect" | "contradictory" | "didnt_answer" | "source_issue" | "other",
    reasonDetail?: string
  ): Promise<void> {
    const res = await authFetch(`/api/chat/feedback/reason`, {
      method: "POST",
      body: JSON.stringify({ message_id: messageId, reason, reason_detail: reasonDetail, region: REGION }),
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({ detail: "Failed to submit feedback reason" }));
      throw new Error(errorData.detail || "Failed to submit feedback reason");
    }
  },

  /** Compare a stored governing-documents answer (by message id — the server loads the question,
   *  answer and passages itself; a retry with the same id reuses the frozen context) with Florida law (statute answer + a three-section
   *  comparison). `communityType` is only a hint: the server resolves the regime itself and reports
   *  the one it used ("unknown" when it could not tell) rather than guessing. */
  async compareWithLaw(
    documentMessageId: number,
    documentIds?: number[],
    communityType?: "hoa_poa" | "condo" | "unsure" | null,
  ): Promise<{
    law_answer: string;
    /** null when no statute applies (status "no_applicable_source"). */
    comparison_summary: string | null;
    community_type: "hoa_poa" | "condo" | "unknown";
    status?: "ok" | "no_applicable_source" | "incomplete";
    /** "law_only": a separate statutory answer ("Check applicable law"), not a comparison. */
    mode?: "comparison" | "law_only";
    /** Trusted references (statute links from the server's own sources; document pages searched). */
    references?: Array<{ source_id: string; kind: "statute" | "user_document"; locator?: string | null; url?: string | null; filename?: string | null; law_current_through?: string | null }>;
    reference_links?: string[];
    referenced_documents?: Array<Record<string, unknown>>;
  }> {
    const res = await authFetch("/api/florida/v1/user-documents/compare-with-law", {
      method: "POST",
      body: JSON.stringify({
        document_message_id: documentMessageId,
        document_ids: documentIds,
        community_type: communityType ?? undefined,
      }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      // Compare is part of the document question it compares, so it shares that question's free
      // allowance; past it the server answers 402.
      if (res.status === 402) {
        throw new Error(
          body?.detail?.code === "free_questions_exhausted"
            ? "You have used all of your free questions. Subscribe to compare your documents with the law."
            : "Comparing with the law requires an active subscription.",
        );
      }
      throw new Error(
        (typeof body?.detail === "string" && body.detail) ||
          "We couldn't compare this answer with the law right now. Please try again.",
      );
    }
    return res.json();
  },

  async getAttachmentUrl(key: string): Promise<string> {
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");
    const token = await user.getIdToken();
    const res = await fetch(`${API_BASE_URL}/api/chat/attachment-url?key=${encodeURIComponent(key)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error("Failed to get attachment URL");
    const data = await res.json();
    return data.url;
  },

  /** SSE streaming URL for the Florida chat endpoint (used with fetch-event-source).
   *  v1 = today's route; v2 = same logic, adds recommended_actions[] and no
   *  new entitlement gate (see chatApiVersion in config/features). */
  getFloridaStreamUrl(): string {
    return chatApiVersion === "v2"
      ? `${API_BASE_URL}/api/florida/v2/chat/stream`
      : `${API_BASE_URL}/api/florida/v1/chat/stream`;
  },

  // ==================== FORMS ENDPOINTS ====================

  /** Discover all forms available for a statute section. */
  async listFormsForSection(region: string, section: string): Promise<{
    region: string;
    section: string;
    forms: Array<{
      form_code: string;
      title: string;
      description: string;
      display_citation: string;
      actions: string[];
      delivery_strategy: string;
      form_kind: string;
    }>;
  }> {
    const response = await authFetch(
      `/api/forms/${encodeURIComponent(region)}/sections/${encodeURIComponent(section)}/forms`
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to list forms for section");
    }
    return response.json();
  },

  /** Get the full manifest (schema, ui_schema, metadata) for a form. */
  /** ``use`` marks this fetch as a guided drawer opening (recorded as form
   *  use); plain reads (view text) omit it. */
  async getFormManifest(region: string, formCode: string, use?: FormUseFields) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(use ?? {})) {
      if (value !== undefined && value !== null) params.set(key, String(value));
    }
    const query = params.toString();
    const response = await authFetch(
      `/api/forms/${encodeURIComponent(region)}/${encodeURIComponent(formCode)}/manifest${query ? `?${query}` : ""}`
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to get form manifest");
    }
    return response.json();
  },

  /** Mint a download URL for a static form artifact (called on click, not during chat). */
  async getFormDownloadUrl(region: string, formCode: string, use?: FormUseFields): Promise<{
    url: string;
    filename: string;
    content_type: string;
    expires_in: number | null;
    external: boolean;
  }> {
    const response = await authFetch(
      `/api/forms/${encodeURIComponent(region)}/${encodeURIComponent(formCode)}/download-url`,
      use ? { method: "POST", body: JSON.stringify(use) } : { method: "POST" }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to get download URL");
    }
    return response.json();
  },

  /** Tolerant preview — returns markdown + warnings, no file artifacts. */
  async previewForm(
    region: string,
    formCode: string,
    inputs: Record<string, unknown>,
    options: { fiscal_year_end?: string; calc_method?: string } & FormUseFields = {}
  ): Promise<{ preview_markdown: string; warnings: string[] }> {
    const response = await authFetch(
      `/api/forms/${encodeURIComponent(region)}/${encodeURIComponent(formCode)}/preview`,
      {
        method: "POST",
        body: JSON.stringify({ inputs, ...options }),
      }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Preview failed");
    }
    return response.json();
  },

  /**
   * Download a generated form file. Generated-form URLs may be either an
   * absolute S3 presigned URL (production) or a relative /api/forms/files/...
   * path (local dev) that requires the auth header. Handles both.
   */
  async downloadGeneratedFile(url: string, filename: string): Promise<void> {
    const isAbsolute = /^https?:\/\//i.test(url);
    const response = isAbsolute ? await fetch(url) : await authFetch(url);
    if (!response.ok) {
      throw new Error(`Download failed: ${response.status} ${response.statusText}`);
    }
    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = filename;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(objectUrl);
  },

  /** Strict final generate. Throws with field error details on 422. */
  async generateForm(
    region: string,
    formCode: string,
    inputs: Record<string, unknown>,
    options: { fiscal_year_end?: string; calc_method?: string } & FormUseFields = {}
  ) {
    const response = await authFetch(
      `/api/forms/${encodeURIComponent(region)}/${encodeURIComponent(formCode)}/generate`,
      {
        method: "POST",
        body: JSON.stringify({ inputs, ...options }),
      }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      const msg = (err.detail as string) || "Generation failed";
      const error = Object.assign(new Error(msg), { status: response.status, detail: err.detail });
      throw error;
    }
    return response.json();
  },

  // ==================== ADMIN ENDPOINTS ====================

  async getAdminUsers(params: {
    region?: 'ca' | 'tx' | 'fl';
    search?: string;
    page?: number;
    pageSize?: number;
  }): Promise<AdminUsersListResponse> {
    const queryParams = new URLSearchParams();
    if (params.region) queryParams.append('region', params.region);
    if (params.search) queryParams.append('search', params.search);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.pageSize) queryParams.append('page_size', params.pageSize.toString());

    const response = await authFetch(`/api/admin/users?${queryParams}`);

    if (response.status === 403) {
      throw new Error("Admin access required");
    }
    if (response.status === 401) {
      throw new Error("Session expired. Please log in again.");
    }
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: "Failed to fetch users" }));
      throw new Error(errorData.detail || "Failed to fetch users");
    }

    return response.json();
  },

  async getAdminUserDetails(userId: string, region?: string): Promise<AdminUser> {
    const queryParams = region ? `?region=${region}` : '';

    const response = await authFetch(`/api/admin/users/${userId}${queryParams}`);

    if (response.status === 403) {
      throw new Error("Admin access required");
    }
    if (response.status === 401) {
      throw new Error("Session expired. Please log in again.");
    }
    if (response.status === 404) {
      throw new Error("User not found");
    }
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: "Failed to fetch user details" }));
      throw new Error(errorData.detail || "Failed to fetch user details");
    }

    return response.json();
  },

  async getAdminUserQA(userId: string, region?: string, chatId?: number): Promise<AdminUserQAResponse> {
    const queryParams = new URLSearchParams();
    if (region) queryParams.append('region', region);
    if (chatId) queryParams.append('chat_id', chatId.toString());

    const response = await authFetch(`/api/admin/users/${userId}/qa?${queryParams}`);

    if (response.status === 403) {
      throw new Error("Admin access required");
    }
    if (response.status === 401) {
      throw new Error("Session expired. Please log in again.");
    }
    if (response.status === 404) {
      throw new Error("User not found");
    }
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: "Failed to fetch Q&A" }));
      throw new Error(errorData.detail || "Failed to fetch Q&A");
    }

    return response.json();
  },

  async deleteAdminUser(
    userId: string,
    region?: 'ca' | 'tx' | 'fl'
  ): Promise<{ user_id: string; region: string; success: boolean; message: string }> {
    const queryParams = region ? `?region=${region}` : '';

    const response = await authFetch(`/api/admin/users/${userId}${queryParams}`, {
      method: "DELETE",
    });

    if (response.status === 400) {
      const errorData = await response.json().catch(() => ({}));
      const msg = errorData.detail || errorData.message || "Cannot delete your own account";
      throw new Error(typeof msg === "string" ? msg : "Cannot delete your own account");
    }
    if (response.status === 403) {
      throw new Error("Admin access required");
    }
    if (response.status === 401) {
      throw new Error("Session expired. Please log in again.");
    }
    if (response.status === 404) {
      const errorData = await response.json().catch(() => ({}));
      const msg = errorData.detail || errorData.message || "User not found";
      throw new Error(typeof msg === "string" ? msg : "User not found");
    }
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: "Delete failed" }));
      const msg = errorData.detail || errorData.message || "Delete failed. Try again.";
      throw new Error(typeof msg === "string" ? msg : "Delete failed. Try again.");
    }

    return response.json();
  },

  async deleteAdminUserMessages(
    userId: string,
    region: 'ca' | 'tx' | 'fl',
    chatId?: number
  ): Promise<{ user_id: string; region: string; deleted_count: number; success: boolean; message: string }> {
    const params = new URLSearchParams();
    params.set('region', region);
    if (chatId != null) {
      params.set('chat_id', String(chatId));
    }
    const query = params.toString();

    const response = await authFetch(
      `/api/admin/users/${userId}/messages${query ? `?${query}` : ''}`,
      { method: "DELETE" }
    );

    if (response.status === 400) {
      const errorData = await response.json().catch(() => ({}));
      const msg = errorData.detail || errorData.message || "Invalid region";
      throw new Error(typeof msg === "string" ? msg : "Invalid region");
    }
    if (response.status === 403) {
      throw new Error("Admin access required");
    }
    if (response.status === 401) {
      throw new Error("Session expired. Please log in again.");
    }
    if (response.status === 404) {
      const errorData = await response.json().catch(() => ({}));
      const msg = errorData.detail || errorData.message || "User not found";
      throw new Error(typeof msg === "string" ? msg : "User not found");
    }
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: "Failed to delete messages" }));
      const msg = errorData.detail || errorData.message || "Failed to delete messages.";
      throw new Error(typeof msg === "string" ? msg : "Failed to delete messages.");
    }

    return response.json();
  },

  async getAdminUserAcquisition(userId: string, region?: string): Promise<AdminUserAcquisitionResponse> {
    const query = region ? `?region=${region}` : '';
    const response = await authFetch(`/api/admin/users/${userId}/acquisition${query}`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: "Failed to fetch acquisition" }));
      throw new Error(errorData.detail || "Failed to fetch acquisition");
    }
    return response.json();
  },

  async getAdminUserActivity(userId: string, region?: string): Promise<AdminUserActivityResponse> {
    const query = region ? `?region=${region}` : '';
    const response = await authFetch(`/api/admin/users/${userId}/activity${query}`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: "Failed to fetch activity" }));
      throw new Error(errorData.detail || "Failed to fetch activity");
    }
    return response.json();
  },

  async getAdminUserJourney(userId: string, region?: string): Promise<AdminUserJourneyResponse> {
    const query = region ? `?region=${region}` : '';
    const response = await authFetch(`/api/admin/users/${userId}/journey${query}`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: "Failed to fetch journey" }));
      throw new Error(errorData.detail || "Failed to fetch journey");
    }
    return response.json();
  },

  async getAdminUserDocuments(userId: string, region?: string): Promise<AdminUserDocumentsResponse> {
    const query = region ? `?region=${region}` : '';
    const response = await authFetch(`/api/admin/users/${userId}/documents${query}`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: "Failed to fetch documents" }));
      throw new Error(errorData.detail || "Failed to fetch documents");
    }
    return response.json();
  },

  async setAdminMessageFeedback(
    messageId: number,
    isLiked: boolean,
    region?: string
  ): Promise<AdminFeedbackResponse> {
    const queryParams = region ? `?region=${region}` : '';

    const response = await authFetch(`/api/admin/messages/${messageId}/feedback${queryParams}`, {
      method: "POST",
      body: JSON.stringify({ is_liked: isLiked }),
    });

    if (response.status === 403) {
      throw new Error("Admin access required");
    }
    if (response.status === 401) {
      throw new Error("Session expired. Please log in again.");
    }
    if (response.status === 404) {
      throw new Error("Message not found");
    }
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: "Failed to update feedback" }));
      throw new Error(errorData.detail || "Failed to update feedback");
    }

    return response.json();
  },

  async getEvaluations(params?: {
    region?: string;
    verdict?: string;
    failure_type?: string;
    run_id?: number;
    rubric_version?: string;
    page?: number;
    page_size?: number;
  }): Promise<EvaluationsListResponse> {
    const qs = new URLSearchParams();
    if (params?.region) qs.set("region", params.region);
    if (params?.verdict) qs.set("verdict", params.verdict);
    if (params?.failure_type) qs.set("failure_type", params.failure_type);
    if (params?.run_id != null) qs.set("run_id", String(params.run_id));
    if (params?.rubric_version) qs.set("rubric_version", params.rubric_version);
    if (params?.page != null) qs.set("page", String(params.page));
    if (params?.page_size != null) qs.set("page_size", String(params.page_size));
    const query = qs.toString() ? `?${qs}` : "";
    const response = await authFetch(`/api/admin/evaluations${query}`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || "Failed to fetch evaluations");
    }
    return response.json();
  },

  /**
   * User-reported feedback, browsable/filterable by an admin ("User Feedback"
   * page) — distinct from getEvaluations' automated-judge QA data, and from
   * setAdminMessageFeedback's single-message admin override. Route lives under
   * the chat feedback router (/api/chat/...), NOT the /api/admin/... family
   * getEvaluations/getProposals use — easy to mix up since the query-string
   * building style below mirrors getEvaluations, but the endpoint doesn't.
   */
  async getAdminFeedback(params?: {
    region?: string;
    rating?: "negative" | "positive" | "all";
    reason?: FeedbackReasonValue;
    /** Review state -- applies to rating="negative" only (the server rejects it otherwise). */
    status?: FeedbackReviewStatus;
    page?: number;
    page_size?: number;
  }): Promise<AdminFeedbackListResponse> {
    const qs = new URLSearchParams();
    if (params?.region) qs.set("region", params.region);
    if (params?.rating) qs.set("rating", params.rating);
    if (params?.reason) qs.set("reason", params.reason);
    if (params?.status) qs.set("status", params.status);
    if (params?.page != null) qs.set("page", String(params.page));
    if (params?.page_size != null) qs.set("page_size", String(params.page_size));
    const query = qs.toString() ? `?${qs}` : "";
    const response = await authFetch(`/api/chat/admin/feedback${query}`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || "Failed to fetch user feedback");
    }
    return response.json();
  },

  /** Count-only: negative feedback still needing review, per region (three COUNT queries server-side). */
  async getAdminFeedbackSummary(): Promise<AdminFeedbackSummary> {
    const response = await authFetch(`/api/chat/admin/feedback/summary`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || "Failed to fetch feedback summary");
    }
    return response.json();
  },

  /** Mark negative feedback reviewed (or unreviewed again). Region is explicit per item. */
  async reviewAdminFeedback(items: FeedbackReviewRef[], reviewed: boolean): Promise<FeedbackReviewResponse> {
    const response = await authFetch(`/api/chat/admin/feedback/review`, {
      method: "POST",
      body: JSON.stringify({ items, reviewed }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || "Failed to update feedback review state");
    }
    return response.json();
  },

  async getEvaluationRuns(limit?: number): Promise<EvaluationRun[]> {
    const qs = limit != null ? `?limit=${limit}` : "";
    const response = await authFetch(`/api/admin/evaluation-runs${qs}`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || "Failed to fetch evaluation runs");
    }
    return response.json();
  },

  async evaluateSingle(answerMessageId: number, region: string, force = false): Promise<QAEvaluationResult> {
    const response = await authFetch(`/api/admin/evaluate/single`, {
      method: "POST",
      body: JSON.stringify({ answer_message_id: answerMessageId, region, force }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || "Evaluation failed");
    }
    return response.json();
  },

  async triggerBatchEvaluation(): Promise<BatchTriggerResponse> {
    const response = await authFetch(`/api/admin/evaluate/trigger-batch`, {
      method: "POST",
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || "Failed to trigger batch evaluation");
    }
    return response.json();
  },

  async getProposals(params?: {
    status?: string;
    failure_type?: string;
    region?: string;
    page?: number;
    page_size?: number;
  }): Promise<ProposalsListResponse> {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.failure_type) qs.set("failure_type", params.failure_type);
    if (params?.region) qs.set("region", params.region);
    if (params?.page != null) qs.set("page", String(params.page));
    if (params?.page_size != null) qs.set("page_size", String(params.page_size));
    const query = qs.toString() ? `?${qs}` : "";
    const response = await authFetch(`/api/admin/proposals${query}`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || "Failed to fetch proposals");
    }
    return response.json();
  },

  async getProposalDetail(proposalId: number): Promise<ImprovementProposal> {
    const response = await authFetch(`/api/admin/proposals/${proposalId}`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || "Failed to fetch proposal");
    }
    return response.json();
  },

  async updateProposalStatus(proposalId: number, update: ProposalStatusUpdate): Promise<ImprovementProposal> {
    const response = await authFetch(`/api/admin/proposals/${proposalId}/status`, {
      method: "PATCH",
      body: JSON.stringify(update),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || "Failed to update proposal status");
    }
    return response.json();
  },

  async getEvalDigest(window: "7d" | "30d" | "90d" | "all" = "7d"): Promise<EvalDigestResponse> {
    const response = await authFetch(`/api/admin/eval-digest?window=${window}`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || "Failed to fetch eval digest");
    }
    return response.json();
  },

  // -------------------------------------------------------------------
  // Marketing Intelligence — /api/admin/marketing-*, /api/admin/marketing-analysis/*
  // -------------------------------------------------------------------

  async listMarketingTasks(params: {
    status_filter?: string;
    region?: string;
    primary_category?: string;
    initiative_key?: string;
  } = {}): Promise<MarketingTask[]> {
    const q = new URLSearchParams();
    if (params.status_filter) q.append("status_filter", params.status_filter);
    if (params.region) q.append("region", params.region);
    if (params.primary_category) q.append("primary_category", params.primary_category);
    if (params.initiative_key) q.append("initiative_key", params.initiative_key);
    const response = await authFetch(`/api/admin/marketing-tasks?${q}`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to load marketing tasks");
    }
    return response.json();
  },

  async createMarketingTask(params: MarketingTaskCreate): Promise<MarketingTask> {
    const response = await authFetch("/api/admin/marketing-tasks", {
      method: "POST",
      body: JSON.stringify(params),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to create task");
    }
    return response.json();
  },

  async startMarketingTask(
    taskId: number,
    params: { baseline_metrics?: Record<string, unknown>; baseline_window_start?: string; baseline_window_end?: string } = {}
  ): Promise<MarketingTaskExecution> {
    const response = await authFetch(`/api/admin/marketing-tasks/${taskId}/start`, {
      method: "POST",
      body: JSON.stringify(params),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to start task");
    }
    return response.json();
  },

  async completeMarketingTask(
    taskId: number,
    params: MarketingTaskCompletion = {}
  ): Promise<MarketingTaskExecution> {
    const response = await authFetch(`/api/admin/marketing-tasks/${taskId}/complete`, {
      method: "POST",
      body: JSON.stringify(params),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to complete task");
    }
    return response.json();
  },

  async deferMarketingTask(taskId: number, reason?: string): Promise<MarketingTask> {
    const response = await authFetch(`/api/admin/marketing-tasks/${taskId}/defer`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to defer task");
    }
    return response.json();
  },

  async dismissMarketingTask(taskId: number, reason?: string): Promise<MarketingTask> {
    const response = await authFetch(`/api/admin/marketing-tasks/${taskId}/dismiss`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to dismiss task");
    }
    return response.json();
  },

  async createMarketingManualEvidence(params: ManualEvidenceCreate): Promise<ManualEvidenceResponse> {
    const response = await authFetch("/api/admin/marketing-manual-evidence", {
      method: "POST",
      body: JSON.stringify(params),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to add manual evidence");
    }
    return response.json();
  },

  async listMarketingTaskComments(taskId: number): Promise<MarketingTaskComment[]> {
    const response = await authFetch(`/api/admin/marketing-tasks/${taskId}/comments`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to load comments");
    }
    return response.json();
  },

  async createMarketingTaskComment(taskId: number, text: string): Promise<MarketingTaskComment> {
    const response = await authFetch(`/api/admin/marketing-tasks/${taskId}/comments`, {
      method: "POST",
      body: JSON.stringify({ text }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to add comment");
    }
    return response.json();
  },

  /** Page inventory search for the New-article "Existing page" picker (admin-only). */
  async listSeoPages(params: { region?: string; q?: string; limit?: number } = {}): Promise<MarketingSeoPage[]> {
    const qs = new URLSearchParams();
    if (params.region) qs.set("region", params.region);
    if (params.q) qs.set("q", params.q);
    if (params.limit != null) qs.set("limit", String(params.limit));
    const query = qs.toString() ? `?${qs}` : "";
    const response = await authFetch(`/api/admin/seo/pages${query}`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to search pages");
    }
    return response.json();
  },

  async listMarketingGoals(): Promise<MarketingGoal[]> {
    const response = await authFetch("/api/admin/marketing-goals");
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to load goals");
    }
    return response.json();
  },

  async patchMarketingGoal(goalKey: string, params: Record<string, unknown>): Promise<MarketingGoal> {
    const response = await authFetch(`/api/admin/marketing-goals/${encodeURIComponent(goalKey)}`, {
      method: "PATCH",
      body: JSON.stringify(params),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to update goal");
    }
    return response.json();
  },

  async listMarketingInitiatives(): Promise<MarketingInitiative[]> {
    const response = await authFetch("/api/admin/marketing-initiatives");
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to load initiatives");
    }
    return response.json();
  },

  async listMarketingStrategyDecisions(decisionStatus: string = "pending"): Promise<MarketingStrategyDecision[]> {
    const q = new URLSearchParams();
    if (decisionStatus) q.append("decision_status", decisionStatus);
    const response = await authFetch(`/api/admin/marketing-strategy-decisions?${q}`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to load strategy decisions");
    }
    return response.json();
  },

  async acceptMarketingStrategyDecision(decisionId: number): Promise<MarketingStrategyDecision> {
    const response = await authFetch(`/api/admin/marketing-strategy-decisions/${decisionId}/accept`, {
      method: "POST",
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to accept decision");
    }
    return response.json();
  },

  async dismissMarketingStrategyDecision(decisionId: number, reviewDate?: string): Promise<MarketingStrategyDecision> {
    const response = await authFetch(`/api/admin/marketing-strategy-decisions/${decisionId}/dismiss`, {
      method: "POST",
      body: JSON.stringify({ review_date: reviewDate }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to dismiss decision");
    }
    return response.json();
  },

  async triggerMarketingAnalysis(): Promise<AnalysisRunTriggerResponse> {
    const response = await authFetch("/api/admin/marketing-analysis/runs", { method: "POST" });
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to start analysis run");
    }
    return response.json();
  },

  async listMarketingAnalysisRuns(limit: number = 20): Promise<MarketingAnalysisRun[]> {
    const response = await authFetch(`/api/admin/marketing-analysis/runs?limit=${limit}`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to load analysis runs");
    }
    return response.json();
  },

  async getMarketingAnalysisRun(runId: number): Promise<MarketingAnalysisRun> {
    const response = await authFetch(`/api/admin/marketing-analysis/runs/${runId}`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to load analysis run");
    }
    return response.json();
  },

  async getMarketingAnalysisRunTasks(runId: number): Promise<MarketingRunTask[]> {
    const response = await authFetch(`/api/admin/marketing-analysis/runs/${runId}/tasks`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to load run tasks");
    }
    return response.json();
  },

  // -------------------------------------------------------------------
  // SEO & Answer Visibility — /api/admin/seo/*
  // -------------------------------------------------------------------

  async listSeoOpportunities(region?: string): Promise<SeoOpportunity[]> {
    const q = new URLSearchParams();
    if (region) q.append("region", region);
    const response = await authFetch(`/api/admin/seo/opportunities?${q}`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to load opportunities");
    }
    return response.json();
  },

  async getSeoSearchPerformance(): Promise<SeoSearchPerformanceResponse> {
    const response = await authFetch("/api/admin/seo/search-performance");
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to load search performance");
    }
    return response.json();
  },

  async getSeoContentCoverage(): Promise<SeoContentCoverageResponse> {
    const response = await authFetch("/api/admin/seo/content-coverage");
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to load content coverage");
    }
    return response.json();
  },

  async listSeoCompetitors(competitorDomain?: string): Promise<SeoCompetitorPage[]> {
    const q = new URLSearchParams();
    if (competitorDomain) q.append("competitor_domain", competitorDomain);
    const response = await authFetch(`/api/admin/seo/competitors?${q}`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((err.detail as string) || "Failed to load competitors");
    }
    return response.json();
  },
};

export default fastApiClient;

export type {
  StripeCustomer,
  StripeSubscription,
  PaymentHistory,
  UpcomingInvoice,
  ChatbotResponse,
  DocumentUploadResponse,
  UserProfile,
  HOAUpdateResponse,
  AdminFeedbackResponse,
};

export type {
  AdminUser,
  AdminUsersListResponse,
  AdminUserQAResponse,
  AdminQAPair,
  EducationPartnerCode,
  EducationPartnerCodeCreate,
  EducationPartnerCodeUpdate,
  HealthRegion,
  HealthCheckEntry,
  HealthOverallState,
  HealthStatusResponse,
  HealthRunResponse,
  HealthActivityEvent,
  HealthActivityEventType,
  HealthHistoryResponse,
  HealthHistoryActivityResponse,
  HealthHistoryAllResponse,
  HealthRegionDiagnostic,
  HealthSchemaDiagnostic,
  HealthDiagnosticsResponse,
  EmailPreferenceStats,
  AdminFeedbackSummary,
  FeedbackReviewStatus,
  FeedbackReviewRef,
  FeedbackReviewResponse,
  MarketingGoalCoverage,
  MarketingMeasurementState,
  MarketingMeasurementWindow,
  MarketingSeoPage,
  MarketingTaskCompletion,
  AdminEmailStats,
  AdminEmailHealthSnapshot,
  AdminEmailBacklogSnapshot,
  AdminEmailActivityItem,
  AdminEmailActivityResponse,
  EmailStage,
  AdminEmailPerformanceRow,
  AdminEmailPerformanceTrendPoint,
  AdminEmailPerformanceResponse,
  MarketingTask,
  MarketingTaskCreate,
  MarketingTaskExecution,
  MarketingTaskStatus,
  MarketingTaskType,
  MarketingGoal,
  MarketingGoalType,
  MarketingGoalTargetType,
  MarketingInitiative,
  MarketingStrategyDecision,
  MarketingDecisionScope,
  MarketingRecommendedAction,
  MarketingDecisionStatus,
  MarketingAnalysisRun,
  MarketingRunStatus,
  MarketingRunTask,
  AnalysisRunTriggerResponse,
  ManualEvidenceCreate,
  ManualEvidenceResponse,
  MarketingTaskComment,
  SeoOpportunity,
  SeoSearchPerformanceRow,
  SeoSearchPerformanceResponse,
  SeoContentCoverageRow,
  SeoContentCoverageResponse,
  SeoCompetitorPage,
} from "@/types/admin";
